import { useMutation, useQuery, useQueryClient } from 'react-query';

import { apiClient } from 'src/services/api/client';
import { queryKeys } from 'src/services/api/constants';
import type { TourSlotSuggestion } from 'src/services/tour-availability/types';

export const TOUR_SUGGESTIONS_PATH = 'api/landlord/tour-suggestions';

type SuggestionResponse = {
    tour_slot_suggestion: TourSlotSuggestion | null;
};

export enum ETourSuggestionStatus {
    SELECTED = 'selected',
    DISMISSED = 'dismissed',
    REJECTED = 'rejected',
}

export const getOpenTourSuggestionQueryKey = (leadUuid: string) => [
    queryKeys.tourSlotSuggestion,
    leadUuid,
];

export const useOpenTourSuggestion = (leadUuid: string, enabled = true) =>
    useQuery(
        getOpenTourSuggestionQueryKey(leadUuid),
        async () => {
            const response = await apiClient(`${TOUR_SUGGESTIONS_PATH}/open`, {
                searchParams: { lead_uuid: leadUuid },
            }).json<SuggestionResponse>();
            return response.tour_slot_suggestion;
        },
        {
            enabled: enabled && !!leadUuid,
            retry: () => false,
        }
    );

export const useCreateTourSuggestions = (leadUuid: string) => {
    const queryClient = useQueryClient();

    return useMutation<TourSlotSuggestion | null, unknown, { messageUuid: string }>(
        async ({ messageUuid }) => {
            const response = await apiClient(`${TOUR_SUGGESTIONS_PATH}/suggestions`, {
                method: 'post',
                json: { message_uuid: messageUuid },
            }).json<SuggestionResponse>();
            return response.tour_slot_suggestion;
        },
        {
            onSuccess: () =>
                queryClient.invalidateQueries(getOpenTourSuggestionQueryKey(leadUuid)),
        }
    );
};

/**
 * Move a suggestion through its lifecycle via the single state-machine endpoint
 * (`PATCH /<id>` with `{ status }`). `selected` keeps the offer live and returns the
 * still-open suggestion; `dismissed` (landlord judged it unhelpful) and `rejected` (lead
 * declined) close it and return null. The backend rejects illegal transitions with 400.
 */
export const useTransitionTourSuggestion = (leadUuid: string) => {
    const queryClient = useQueryClient();

    return useMutation<
        TourSlotSuggestion | null,
        unknown,
        { suggestionId: number; status: ETourSuggestionStatus }
    >(
        async ({ suggestionId, status }) => {
            const response = await apiClient(`${TOUR_SUGGESTIONS_PATH}/${suggestionId}`, {
                method: 'patch',
                json: { status },
            }).json<SuggestionResponse>();
            return response.tour_slot_suggestion;
        },
        {
            onSuccess: () =>
                queryClient.invalidateQueries(getOpenTourSuggestionQueryKey(leadUuid)),
        }
    );
};
